/**
 * Retry utility with exponential backoff for production resilience
 */

export interface RetryOptions {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: Error) => boolean;
  onRetry?: (attempt: number, error: Error, delayMs: number) => void;
}

export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  attempts: number;
  totalTimeMs: number;
}

/**
 * Retries an async operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<RetryResult<T>> {
  const {
    maxAttempts = 3,
    initialDelayMs = 1000,
    maxDelayMs = 10000,
    backoffMultiplier = 2,
    shouldRetry = defaultShouldRetry,
    onRetry,
  } = options;

  const startTime = Date.now();
  let lastError: Error | undefined;
  let attempt = 0;

  while (attempt < maxAttempts) {
    attempt++;

    try {
      const result = await operation();
      return {
        success: true,
        data: result,
        attempts: attempt,
        totalTimeMs: Date.now() - startTime,
      };
    } catch (error: any) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // If this is the last attempt or error is not retryable, fail
      if (attempt >= maxAttempts || !shouldRetry(lastError)) {
        break;
      }

      // Calculate delay with exponential backoff
      const baseDelay = initialDelayMs * Math.pow(backoffMultiplier, attempt - 1);
      const jitter = Math.random() * 0.3 * baseDelay; // Add 0-30% jitter
      const delayMs = Math.min(baseDelay + jitter, maxDelayMs);

      // Notify about retry
      if (onRetry) {
        onRetry(attempt, lastError, delayMs);
      }

      // Wait before retrying
      await sleep(delayMs);
    }
  }

  // All attempts failed
  return {
    success: false,
    error: lastError,
    attempts: attempt,
    totalTimeMs: Date.now() - startTime,
  };
}

/**
 * Default retry logic - retry on network errors and 5xx server errors
 */
function defaultShouldRetry(error: Error): boolean {
  const message = error.message.toLowerCase();

  // Network errors
  if (
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('econnrefused') ||
    message.includes('enotfound') ||
    message.includes('fetch failed')
  ) {
    return true;
  }

  // HTTP 5xx errors (server errors)
  if (message.includes('status: 5') || message.includes('500') || message.includes('502') || message.includes('503')) {
    return true;
  }

  // Rate limit errors (429) - should retry with backoff
  if (message.includes('429') || message.includes('rate limit')) {
    return true;
  }

  // Don't retry client errors (4xx except 429)
  return false;
}

/**
 * Sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Circuit breaker for failing APIs
 */
export class CircuitBreaker {
  private failureCount = 0;
  private successCount = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';

  constructor(
    private readonly failureThreshold: number = 5,
    private readonly successThreshold: number = 2,
    private readonly timeoutMs: number = 60000 // 1 minute
  ) {}

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      // Check if timeout has passed
      if (Date.now() - this.lastFailureTime > this.timeoutMs) {
        this.state = 'half-open';
        this.successCount = 0;
      } else {
        throw new Error('Circuit breaker is OPEN - API is temporarily unavailable');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    this.failureCount = 0;

    if (this.state === 'half-open') {
      this.successCount++;
      if (this.successCount >= this.successThreshold) {
        this.state = 'closed';
        this.successCount = 0;
      }
    }
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.failureThreshold) {
      this.state = 'open';
    }
  }

  getState(): { state: 'closed' | 'open' | 'half-open'; failureCount: number; successCount: number } {
    return {
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
    };
  }

  reset(): void {
    this.state = 'closed';
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = 0;
  }
}

/**
 * Error categorization for better handling
 */
export enum ErrorCategory {
  NETWORK = 'network',
  API_LIMIT = 'api_limit',
  API_ERROR = 'api_error',
  VALIDATION = 'validation',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown',
}

export interface CategorizedError {
  category: ErrorCategory;
  message: string;
  originalError: Error;
  isRetryable: boolean;
  userMessage: string;
}

export function categorizeError(error: Error): CategorizedError {
  const message = error.message.toLowerCase();

  // Network errors
  if (
    message.includes('network') ||
    message.includes('econnrefused') ||
    message.includes('enotfound') ||
    message.includes('fetch failed')
  ) {
    return {
      category: ErrorCategory.NETWORK,
      message: error.message,
      originalError: error,
      isRetryable: true,
      userMessage: 'Network connection error. Retrying...',
    };
  }

  // Timeout errors
  if (message.includes('timeout') || message.includes('timed out')) {
    return {
      category: ErrorCategory.TIMEOUT,
      message: error.message,
      originalError: error,
      isRetryable: true,
      userMessage: 'Request timed out. Retrying...',
    };
  }

  // Rate limit errors
  if (message.includes('429') || message.includes('rate limit') || message.includes('too many requests')) {
    return {
      category: ErrorCategory.API_LIMIT,
      message: error.message,
      originalError: error,
      isRetryable: true,
      userMessage: 'API rate limit reached. Waiting before retry...',
    };
  }

  // API errors (5xx)
  if (message.includes('status: 5') || message.includes('500') || message.includes('502') || message.includes('503')) {
    return {
      category: ErrorCategory.API_ERROR,
      message: error.message,
      originalError: error,
      isRetryable: true,
      userMessage: 'API server error. Retrying...',
    };
  }

  // Validation errors
  if (message.includes('invalid') || message.includes('validation')) {
    return {
      category: ErrorCategory.VALIDATION,
      message: error.message,
      originalError: error,
      isRetryable: false,
      userMessage: 'Invalid input or configuration. Please check your settings.',
    };
  }

  // Unknown errors
  return {
    category: ErrorCategory.UNKNOWN,
    message: error.message,
    originalError: error,
    isRetryable: false,
    userMessage: `Error: ${error.message}`,
  };
}

/**
 * Batch retry utility for multiple operations
 */
export async function retryBatch<T>(
  operations: Array<() => Promise<T>>,
  options: RetryOptions & { concurrency?: number } = {}
): Promise<Array<RetryResult<T>>> {
  const { concurrency = 3, ...retryOptions } = options;
  const results: Array<RetryResult<T>> = [];
  const queue = [...operations];

  // Process operations with limited concurrency
  while (queue.length > 0) {
    const batch = queue.splice(0, concurrency);
    const batchResults = await Promise.all(
      batch.map((operation) => retryWithBackoff(operation, retryOptions))
    );
    results.push(...batchResults);
  }

  return results;
}
